import { SearchLg } from "untitledui-js/react";
import { twMerge } from "tailwind-merge";

import { IconButton } from "./IconButton";
import { Loader } from "./Loader";
import { useEffect, useRef, useState } from "react";
import { SearchBarProps } from "../types/SearchTypes";
import { handleContainerClick } from "../utils/handleContainerClick";

const INPUT_ID = "peopleSearchInput";
const BUTTON_ID = "sendPrompt";

export const SearchBar = ({
  placeholder,
  isLoading,
  isDisabled,
  onClick,
  onInputChange,
  searchValue: controlledSearchValue,
  hasFocus,
}: SearchBarProps) => {
  const [searchValue, setSearchValue] = useState(controlledSearchValue ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync internal state with external controlled value
  useEffect(() => {
    if (controlledSearchValue !== undefined) {
      setSearchValue(controlledSearchValue);
    }
  }, [controlledSearchValue]);

  useEffect(() => {
    if (hasFocus) {
      inputRef.current?.focus();
    }
  }, [hasFocus]);

  // Handle Enter key press to send message
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // Prevent default Enter key behavior
      e.preventDefault();
      // Call the onClick function with the current search value
      onClick(searchValue);
    }
  };

  return (
    <div
      className="flex w-full items-center justify-between rounded-full border border-tertiary-100 bg-white py-1 pr-1 pl-4 focus-within:border-primary-500 dark:border-white/10 dark:bg-white/10"
      role="none"
      onClick={handleContainerClick(BUTTON_ID, INPUT_ID)}
    >
      {/* By default, Hotjar suppresses user input */}
      <input
        type="text"
        placeholder={placeholder}
        aria-label={placeholder}
        value={searchValue}
        id={INPUT_ID}
        ref={inputRef}
        onChange={(e) => {
          setSearchValue(e.target.value);
          onInputChange?.(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        className="w-[85%] leading-[120%] text-tertiary-500 placeholder:text-tertiary-500 focus:outline-none dark:text-white/80 dark:placeholder:text-white/80"
      />
      <IconButton
        ariaLabel={placeholder}
        id={BUTTON_ID}
        icon={isLoading ? <Loader /> : <SearchLg />}
        disabled={isDisabled}
        className={twMerge(
          "bg-primary-500 text-secondary-900 transition-all duration-20 dark:bg-transparent dark:text-white",
          "hover:bg-primary-400 hover:text-secondary-950 dark:hover:bg-white/30 dark:hover:text-white",
          "disabled:bg-tertiary-200 dark:disabled:bg-tertiary-50 dark:disabled:text-tertiary-300"
        )}
        onClick={() => onClick(searchValue)}
      />
    </div>
  );
};
